<template>
  <div>
    <b-overlay
      :show="busy"
      rounded
      opacity="0.6"
      spinner-small
      spinner-variant="secondary"
      class="d-inline-block"
    >
      <b-button
        v-bind="$attrs"
        @click="showModal"
        variant="secondary"
        style="padding: 1px 12px;"
      >
        Pagou
      </b-button>
    </b-overlay>

    <b-modal
      v-model="showDialog"
      title="Marcar boleto como pago"
      @ok="handleSubmit"
      @cancel="resetForm"
    >
      <b-form>
        <div class="mb-3">
          <small class="text-muted">
            Valor do boleto: {{ formatCurrency(invoice.boletoVeins) }}
          </small>
        </div>

        <b-form-group
          label="Multa e Juros (R$)"
          label-for="fineAndInterest"
          :state="validationState.fineAndInterest"
          :invalid-feedback="validationFeedback.fineAndInterest"
        >
          <money
            v-model="form.fineAndInterest"
            v-bind="money"
            class="form-control"
            :class="{ 'is-invalid': validationState.fineAndInterest === false }"
          />
        </b-form-group>

        <b-form-group
          label="Desconto (R$)"
          label-for="discount"
          :state="validationState.discount"
          :invalid-feedback="validationFeedback.discount"
        >
          <money
            v-model="form.discount"
            v-bind="money"
            class="form-control"
            :class="{ 'is-invalid': validationState.discount === false }"
          />
        </b-form-group>

        <div class="mt-3">
          <small class="text-muted">
            Valor a ser pago: {{ calculatedAmount }}
          </small>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { Api } from '../../../../vue/api';
import { Money } from 'v-money2';

const API = new Api();

export default {
  components: {
    Money,
  },
  props: {
    invoice: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      busy: false,
      showDialog: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: false,
        allowBlank: true,
        min: 0,
      },
      form: {
        fineAndInterest: 0,
        discount: 0,
      },
      validationState: {
        fineAndInterest: null,
        discount: null,
      },
      validationFeedback: {
        fineAndInterest: '',
        discount: '',
      },
    };
  },
  computed: {
    calculatedAmount() {
      const baseAmount = this.invoice.boletoVeins || 0;
      const fineAndInterest = this.form.fineAndInterest || 0;
      const discount = this.form.discount || 0;

      return this.formatCurrency(baseAmount + fineAndInterest - discount);
    },
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);
    },
    showModal() {
      this.showDialog = true;
    },
    resetForm() {
      this.form = {
        fineAndInterest: 0,
        discount: 0,
      };
      this.validationState = {
        fineAndInterest: null,
        discount: null,
      };
      this.validationFeedback = {
        fineAndInterest: '',
        discount: '',
      };
    },
    validateForm() {
      let isValid = true;

      // Validar que não pode ter multa/juros e desconto ao mesmo tempo
      if (this.form.fineAndInterest > 0 && this.form.discount > 0) {
        this.validationState.fineAndInterest = false;
        this.validationState.discount = false;
        this.validationFeedback.fineAndInterest =
          'Não é possível aplicar multa/juros e desconto simultaneamente';
        this.validationFeedback.discount =
          'Não é possível aplicar multa/juros e desconto simultaneamente';
        isValid = false;
      } else {
        // Validar multa e juros
        if (this.form.fineAndInterest < 0) {
          this.validationState.fineAndInterest = false;
          this.validationFeedback.fineAndInterest =
            'Valor deve ser maior ou igual a zero';
          isValid = false;
        } else {
          this.validationState.fineAndInterest = true;
        }

        // Validar desconto
        if (this.form.discount < 0) {
          this.validationState.discount = false;
          this.validationFeedback.discount =
            'Valor deve ser maior ou igual a zero';
          isValid = false;
        } else if (this.form.discount > this.invoice.boletoVeins) {
          this.validationState.discount = false;
          this.validationFeedback.discount =
            'Desconto não pode ser maior que o valor do boleto';
          isValid = false;
        } else {
          this.validationState.discount = true;
        }
      }

      return isValid;
    },
    async handleSubmit(bvModalEvent) {
      bvModalEvent.preventDefault();

      if (!this.validateForm()) {
        return;
      }

      this.busy = true;
      const { nossoNumero, codigoSolicitacao, id, boletoVeins } = this.invoice;
      const apiConfig = {
        path: codigoSolicitacao
          ? '/v3/invoice/mark-paid'
          : '/v2/invoice/mark-paid',
        data: {
          boletoId: codigoSolicitacao || nossoNumero,
          paidAmount:
            boletoVeins +
            (this.form.fineAndInterest || 0) -
            (this.form.discount || 0),
          fineAndInterest: this.form.fineAndInterest || 0,
          discount: this.form.discount || 0,
        },
      };

      try {
        const { errors, ok } = await API.post('/execute', {
          path: apiConfig.path,
          method: 'post',
          data: apiConfig.data,
        });

        if (!ok) {
          this.$bvToast.toast(errors.message, {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          });
          return;
        }

        this.$bvToast.toast('Boleto marcado como Pago com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        });

        this.$emit('reload', id);
        this.showDialog = false;
        this.resetForm();
      } catch (error) {
        this.$bvToast.toast('Erro ao processar a requisição', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.money {
  text-align: right;
}
</style>
